import {OrcamentoInfoRow} from "@components/orcamento/OrcamentoInfo.tsx";
import {OrcamentoEntry} from "@components/solicitacoes/OrcamentoEntry.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {_SolicitacoesEndpoints, GetSolicitacoes} from "@localization/siteMap/endpoints/_SolicitacoesEnpoints.ts";
import type {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import type {ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.ts";
import type {ISolicitacao, ISolicitacaoItem} from "@models/solicitacoes/solicitacoes.ts";
import {DefaultContainer} from "@shared/DefaultContainer";
import {Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,} from "@mui/material";
import {PermissionableButton} from "@shared/specialComponents/PermissionableButton.tsx";
import React, {useEffect, useMemo, useState} from "react";
import {AddOutlined} from "@mui/icons-material";
import {IOrcamentoFiltroDto} from "@models/solicitacoes/orcamentoDto.ts";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {OrcamentosPageFilters} from "./OrcamentosPageFilters";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {SearchTextField} from "@components/shared/textField/SearchTextField.tsx";
import {SolicitacaoItemInformacaoNavLink} from "@components/solicitacoes/itens/SolicitacaoItemInformacao.tsx";

interface OrcamentosPageProps extends DefaultPageProps {
    overrides?: {
        titulo?: string
        size?: 'small' | 'medium' | 'large'
    },
    filtros?: IOrcamentoFiltroDto,
    slotProps?: {
        orcamentoInfo?: (o: ISolicitacaoOrcamento) => Partial<React.ComponentProps<typeof OrcamentoInfoRow>>
        solicitacaoItemNav?: Partial<React.ComponentProps<typeof SolicitacaoItemInformacaoNavLink>>
    }
}

export function OrcamentosPage(props: OrcamentosPageProps) {
    const {
        overrides,
    } = props;

    const {Get} = useApi()
    const {addPopUp, closePopUp} = useAlert();
    const [orcamentos, setOrcamentos] = useState<ISolicitacaoOrcamento[]>([]);
    const [pagination, setPagination] = useState<IPagination>({
        page: 1,
        pageSize: 10,
    });

    const [filters, setFilters] = useState<IOrcamentoFiltroDto>({...JSON.parse(localStorage.getItem('orcamentos-default-filters') ?? '{}'), ...props.filtros});

    // Todo: Remover depois, é gambiarra
    const [solicitacao, setSolicitacao] = useState<ISolicitacao | null>(null);
    const [solicitacaoItem, setSolicitacaoItem] = useState<ISolicitacaoItem | null>(null);

    const idItem = useMemo(() => {
        if (props.filtros?.idItem == null) return undefined;

        if (props.filtros.idItem.length == 1) return props.filtros.idItem[0];
        return undefined;
    }, [props.filtros?.idItem]);

    useEffect(() => {
        if (pagination.page == 0) {
            setPagination({...pagination, page: 1});
            return;
        }
        setOrcamentos([]);
        setPagination(prevState => ({...prevState, loading: true}))
        Get(_SolicitacoesEndpoints.GetOrcamentos({
            ...filters,
            deveIncluir: ['Fornecedor', 'Aprovacoes', "Item", 'Solicitacao'],
            orderBy: 'UltimaAtualizacao',
        }, pagination)).then(x => {
            if (x.status == 'Success') {
                setOrcamentos(x.items);
                setPagination(prev => ({...prev, ...x.pagination}));
            }
        }).finally(() => {
            setPagination(prevState => ({...prevState, loading: false, ignoreCache: false}))
        })
    }, [pagination.page, pagination.ignoreCache == true]);

    useEffect(() => {
        GetSolicitacoes({
            deveIncluir: ["Itens", "Forms", "UsuarioCriador", "TipoSolicitacao", "Recurso"],
            id: props.filtros?.idSolicitacao,
        }, {
            page: 1,
            pageSize: 1,
        }).then(x => {
            if (x.status == 'Success') {
                setSolicitacao(x.items[0]);
                const t = x.items[0]?.itens?.find(x => x.id == (idItem));
                if (t) setSolicitacaoItem(t);
            }
        });

    }, []);

    return (
        <DefaultContainer titulo={props.hideHeader ? '' : overrides?.titulo ?? 'Orçamentos'}
                          size={overrides?.size ?? 'large'}
                          UpperComponent={<Grid container justifyContent={'space-between'} spacing={1}>
                              <Grid size={{xs: 12, md: 6}}>
                                  <SearchTextField disabled
                                                   label={'Pesquisar orçamentos'}
                                                   hasChanges={filters != null && Object.keys(filters).length > 0}
                                                   pageProps={{pagination, setPagination}}
                                                   onSearch={() => {
                                                   }} openFilters={() => {
                                      addPopUp({
                                          title: 'Filtros de orçamentos',
                                          size: 'lg',
                                          content: <OrcamentosPageFilters filtersProps={{filters, setFilters}}
                                                                          onApply={(x) => {
                                                                              setFilters(x)
                                                                              setPagination({...pagination, page: 0});
                                                                              closePopUp()
                                                                          }}/>,
                                      });
                                  }}/>
                              </Grid>
                              <Grid size={{xs: 12, md: 6}}>
                                  <PermissionableButton permission={["CriarOrcamentoMembro"]}
                                                        showEvenWithoutAccess
                                                        icon={<AddOutlined/>}
                                                        fullHeight
                                                        disabled={!(solicitacao && solicitacaoItem) || ['Aprovado', 'Finalizado'].includes(solicitacaoItem?.status)}
                                                        onClick={() => {
                                                            if (solicitacao && solicitacaoItem) {
                                                                addPopUp({
                                                                    content: <OrcamentoEntry solicitacao={solicitacao}
                                                                                             item={solicitacaoItem}
                                                                                             onCreate={() => {
                                                                                                 setPagination({
                                                                                                     ...pagination,
                                                                                                     page: 0,
                                                                                                     ignoreCache: true
                                                                                                 });
                                                                                                 closePopUp()
                                                                                             }}
                                                                    />,
                                                                    title: 'Criar novo orçamento',
                                                                    size: 'lg',
                                                                })
                                                            }
                                                        }
                                                        }
                                                        label={'Incluir orçamento'}/>
                              </Grid>
                          </Grid>}
                          pageProps={{pagination, setPagination}}
                          component={Paper} spacing={1}>
            <Grid px={1}>
                <Grid>
                    <TableContainer>
                        <Table size={'small'} stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Id</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Fornecedor</TableCell>
                                    <TableCell>Valor Total</TableCell>
                                    <TableCell>Qtd/Solicitada</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Data</TableCell>
                                    <TableCell align={'right'}>Mais</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {orcamentos.map((x) => {
                                    return <OrcamentoInfoRow {...props.slotProps?.orcamentoInfo?.(x)}
                                                             value={x}
                                                             key={x.id}/>;
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </DefaultContainer>
    )
        ;
}

